import type { AdvScene, AdvLine } from './adv'
import { advFix2Active, applyAdvFix2Lines } from './adv_fix2'

// 会話場面の文言の直し6か所 (11-04 作者 wording=ship で配布版の既定)。前の文言は開発版の ?advWording=old。
//   2026-11-04 盲検監査 (資料を渡さず画面だけを見た AI 3人) が挙げた文言のうち、コードで確かめて誤りと言える物だけ。
//   記録 = docs/research/2026-11-04-blind-audit.md。
type Fix = { kind: AdvLine['kind']; from: string; to?: string; speech?: 'spoken' | 'inner' }

const FIXES: readonly Fix[] = [
  // 終幕その2: 境界を渡るのは「越える」。
  { kind: 'narration', from: '舞台の縁を、超えた。', to: '舞台の縁を、越えた。' },
  // 終幕その2: ら抜き。
  { kind: 'lunaire', from: '……出れる。', to: '……出られる。' },
  // 08 の選択1・残るの終わり: 台詞の「」の中の引用は『』。
  { kind: 'lunaire', from: '「こうしてください」って。', to: '『こうしてください』って。' },
  { kind: 'lunaire', from: 'うん、こんな感じでいいか。これなら「私」も迷わないよね。', to: 'うん、こんな感じでいいか。これなら『私』も迷わないよね。' },
  // 04 の選択2: 同じ一言が他の選択では心の声 (（）付き) なのに、ここだけ括弧が無い。
  { kind: 'lunaire', from: '……これで、ちゃんと。', speech: 'inner' },
  // 01 の戦う前の締め: 絵のある場面なので括弧が出ないが、同じ行が終幕 (文字だけの場面) では（）付きで出る。
  //   2026-11-04 Codex 熟練役が「発話か心の声か揺れた」。docs/research/2026-11-04-blind-audit-codex-veteran.md
  { kind: 'lunaire', from: '——終わらせてあげないと。', speech: 'inner' },
]

let advWordingFix = true
export function setAdvWordingFix(on: boolean): void { advWordingFix = on }

// 物語の取り違えを防ぐ直し (?advWording とは別に効く)。同じ監査の物語の問い 01・03・04。a = 意味を変えず誤読だけ防ぐ・b = もう少し踏み込む。
//   配布版 = ship (11-04 作者: s01 = a・s03 = b・s04 = b で2択もそろえる)。前の文は開発版の ?advStory=old。案だけの版は ?advStory=a|b。
//   記録 = docs/research/2026-11-04-blind-audit-story-wording.md。声の行 (adv_voice_v2 が順に差し替える) は足さない・消さない。
export type AdvStoryVariant = 'now' | 'a' | 'b' | 'ship'
type StoryFix = { kind: 'narration'; from: string; to: string; after?: string }
type OptionFix = { from: string; to: string }

const STORY_A: readonly StoryFix[] = [
  // 01: 倒した「影」が、門の上の名札「影」(= 月喰いの王) と同じ者に読める。稽古の相手は名も無き亡霊。
  { kind: 'narration', from: '影は、霧のようにほどけて、消えた。', to: '亡霊は、霧のようにほどけて、消えた。' },
  // 03: 兜が首の上にあるように読める。絵では床に落ちている。
  { kind: 'narration', from: '兜は、扉のほうを向いたままだった。', to: '床の兜は、扉のほうを向いたままだった。' },
  // 04: 「少しずれていた」が、止まっていた針と別の話に読める。
  { kind: 'narration', from: '大時計の針が、少しずれていた。', to: '止まった針が、少しずれていた。' },
]
const STORY_B: readonly StoryFix[] = [
  { kind: 'narration', from: '影は、霧のようにほどけて、消えた。', to: '亡霊は、霧のようにほどけて、消えた。' },
  { kind: 'narration', from: '影は、霧のようにほどけて、消えた。', to: '門の上の影とは、別の者だった。', after: '亡霊は、霧のようにほどけて、消えた。' },
  { kind: 'narration', from: '兜は、扉のほうを向いたままだった。', to: '兜は、床に落ちても、扉のほうを向いていた。' },
  { kind: 'narration', from: '大時計の針が、少しずれていた。', to: '止まった針が、少しずれていた。' },
]
// 作者が選んだ組 (s01 = a・s03 = b・s04 = b)。
const STORY_SHIP: readonly StoryFix[] = [STORY_A[0]!, STORY_B[2]!, STORY_B[3]!]
// 04 の b (配布版も): 2択の「止まった針」を、いま見えている「ずれた針」にそろえる (台帳の8択の表の文も変わる)。
const OPTIONS_B: readonly OptionFix[] = [
  { from: '「止まった針を、進めてみる」', to: '「ずれた針を、進めてみる」' },
  { from: '「止まった針を、婚礼の刻に戻してあげる」', to: '「ずれた針を、婚礼の刻に戻してあげる」' },
]

let advStory: AdvStoryVariant = 'ship'
export function setAdvStoryFix(v: string | null | undefined): AdvStoryVariant {
  return (advStory = v === 'old' ? 'now' : v === 'a' || v === 'b' ? v : 'ship')
}

function applyStoryLines(lines: AdvLine[]): AdvLine[] {
  if (advStory === 'now') return lines
  const fixes = advStory === 'a' ? STORY_A : advStory === 'b' ? STORY_B : STORY_SHIP
  return lines.flatMap(line => {
    if (line.kind !== 'narration') return [line]
    const hit = fixes.filter(f => f.from === line.text)
    if (!hit.length) return [line]
    const main = hit.find(f => !f.after)
    const out: AdvLine[] = [main ? { ...line, text: main.to } : line]
    for (const f of hit) if (f.after) out.push({ kind: 'narration', text: f.to })
    return out
  })
}

function fixLine(line: AdvLine): AdvLine {
  if (line.kind !== 'narration' && line.kind !== 'lunaire') return line
  const f = FIXES.find(x => x.kind === line.kind && x.from === line.text)
  if (!f) return line
  if (line.kind === 'lunaire') {
    // 括弧を足す直しは speech の無い行だけ (inner・spoken が既に付いた行は触らない)。
    if (f.speech && line.speech) return line
    return { ...line, text: f.to ?? line.text, ...(f.speech ? { speech: f.speech } : {}) }
  }
  return f.to ? { ...line, text: f.to } : line
}

export function applyAdvWordingFix(scene: AdvScene): AdvScene {
  if (!advWordingFix && advStory === 'now' && !advFix2Active()) return scene
  // 初見監査 2回目の比較版 (?advFix2=a|b・adv_fix2.ts) は最後に当てる。
  const fix = (ls: AdvLine[]): AdvLine[] => applyAdvFix2Lines(applyStoryLines(advWordingFix ? ls.map(fixLine) : ls), scene.id)
  const lines = fix(scene.lines)
  const choice = scene.choice && {
    ...scene.choice,
    options: scene.choice.options.map(o => {
      const text = advStory === 'b' || advStory === 'ship' ? OPTIONS_B.find(x => x.from === o.text)?.to ?? o.text : o.text
      return { ...o, text, ...(o.resultLines ? { resultLines: fix(o.resultLines) } : {}) }
    }),
  }
  return choice ? { ...scene, lines, choice } : { ...scene, lines }
}
